/**
 * Models the workflow of a Diagnosis Review Group entry form
 */

import Radio from "backbone.radio";

import BaseModel from "./drg";
import ClaimsList from "../../collections/chart-reviews/claims";
import UserCollection from "../../collections/chart-reviews/reviewers/selectable";
import StringKeyValueCollection from "../../collections/key-string-value";
import {
  isValidDate,
  dateDifference,
  isDateSameOrBefore,
  isDateSameOrAfter,
} from "../../utils";

const channel = Radio.channel("global");

const Model = BaseModel.extend({
  namedAttributes: {
    reviewers: UserCollection,
    claimsList: ClaimsList,
    dateDiff: Number,
    canEditDiagCode: Boolean,
    isValidDiagCode: Boolean,
    canFetchMappedHCCs: Boolean,
    canFetchClaims: Boolean,
    canEditClaimNumber: Boolean,
    canEditCommercialPlanId: Boolean,
    canEditActions: Boolean,
    canEditNote: Boolean,
    canSubmitCollection: Boolean,
    canStartEvaluation: Boolean,
    canApproveForSubmission: Boolean,
    statusMessage: String,
    diagMinDate: String,
    diagMaxDate: String,
    didFetchMappedHCCs: Boolean,
    diagnosisAttributesList: StringKeyValueCollection,
    canEditDiagnosisAttributes: Boolean,
  },

  initialize() {
    // do workflow whenever the model changes
    this.listenTo(this, "change", this._doWorkflow);

    this.listenTo(this, "change:fromDateOfService", this._onFromDateChange);
    this.listenTo(this, "change:dateDiff", this._onDateDiffChange);

    // listen for validation messages
    this.listenTo(channel, "validation:diagCode", this._diagCodeValidation);
    channel.reply("isDiagCodeValid", this._isDiagCodeValid, this);

    // set initial values
    this.set({
      canSubmitCollection: false,
      isValidDiagCode: false,
      canStartEvaluation: false,
      canApproveForSubmission: false,
      actionApproveSubmission: false,
      actionStartEvaluation: false,
      claimsList: new ClaimsList(),
      canFetchClaims: false,
      canFetchMappedHCCs: false,
      didFetchMappedHCCs: false,
      diagnosisAttributesList: new StringKeyValueCollection(),
      canEditDiagnosisAttributes: false,
    });
  },

  _diagCodeValidation(valid, diagCode) {
    this.set({
      isValidDiagCode: valid,
      canFetchMappedHCCs: valid,
      didFetchMappedHCCs: false, // reset flag when diag code changes
      mappedHCCs: null,
      diagCode,
    });
  },

  _isDiagCodeValid(code) {
    const { diagCode, isValidDiagCode } = this.toJSON();
    return code === diagCode && isValidDiagCode;
  },

  _areDOSValid() {
    const { fromDateOfService, throughDateOfService } = this.toJSON();
    return isValidDate(fromDateOfService) && isValidDate(throughDateOfService);
  },

  _areDOSWithinMinMaxDates() {
    let { fromDateOfService, throughDateOfService, diagMinDate, diagMaxDate } =
      this.toJSON();

    let modelChanges = {};

    if (diagMinDate) {
      // from date
      if (!isDateSameOrAfter(fromDateOfService, diagMinDate)) {
        // override from date with min date
        fromDateOfService = diagMinDate;
        modelChanges.fromDateOfService = fromDateOfService;
      }
      // thru date
      if (!isDateSameOrAfter(throughDateOfService, diagMinDate)) {
        // override from date with min date
        throughDateOfService = diagMinDate;
        modelChanges.throughDateOfService = throughDateOfService;
      }
    }

    if (diagMaxDate) {
      // from date
      if (!isDateSameOrBefore(fromDateOfService, diagMaxDate)) {
        // override from date with min date
        fromDateOfService = diagMaxDate;
        modelChanges.fromDateOfService = fromDateOfService;
      }
      // thru date
      if (!isDateSameOrBefore(throughDateOfService, diagMaxDate)) {
        // override from date with min date
        throughDateOfService = diagMaxDate;
        modelChanges.throughDateOfService = throughDateOfService;
      }
    }

    if (!_.isEmpty(modelChanges)) {
      this.set(modelChanges);
    }
  },

  _doWorkflow() {
    const {
      fromDateOfService,
      throughDateOfService,
      isValidDiagCode,
      claimNumber,
      commercialPlanId,
      actionApproveSubmission,
    } = this.toJSON();

    let dateDiff = -1;
    if (this._areDOSValid()) {
      // are dates within the min and max
      this._areDOSWithinMinMaxDates();

      // calculate date diff in milliseconds
      const fromDOSDiff = dateDifference("1/1/1970", fromDateOfService);
      const thruDOSDiff = dateDifference("1/1/1970", throughDateOfService);
      dateDiff = fromDOSDiff + thruDOSDiff;
    }

    // diag code can be edited when from/thru dates have been set and
    // dateDiff is zero or more.
    const canEditDiagCode = isDateSameOrBefore(
      fromDateOfService,
      throughDateOfService
    );
    const canFetchClaims = canEditDiagCode;
    // HCCs can be fetched when dates and diag code have been set
    const canFetchMappedHCCs = canFetchClaims && isValidDiagCode;

    const hasClaimNumber = !_.isEmpty(claimNumber);
    const hasCommercialPlanId = !_.isEmpty(commercialPlanId);

    // get reviewers collection
    const reviewers = this.getReviewers();
    let userIsReviewer = false;
    if (reviewers && reviewers.length) {
      userIsReviewer = reviewers.get(channel.request("user:id"));
    }

    const userHasValidRole = channel.request("user:has-any-role", [
      "ROLE_ADMIN",
      "ROLE_CHART_REVIEW_ADMIN",
    ]);
    const userIsValidReviewer = !!userIsReviewer || userHasValidRole;

    this.set({
      dateDiff,
      canEditDiagCode,
      canFetchClaims,
      // the rest of the fields can be edited when HCCs can be fetched
      canEditClaimNumber: canEditDiagCode,
      canEditCommercialPlanId: canFetchMappedHCCs && hasClaimNumber,
      canEditActions: canFetchMappedHCCs,
      canEditNote: canFetchMappedHCCs,
      canApproveForSubmission:
        hasClaimNumber && hasCommercialPlanId && userIsValidReviewer,
      canStartEvaluation: !actionApproveSubmission,
      canEditDiagnosisAttributes: canFetchMappedHCCs,
    });

    // run default validation
    this.isValid();

    // set the statue message to the current validation error
    this.set("statusMessage", this.validationError);
  },

  // Sets through DOS date, when empty, to from DOS date
  _onFromDateChange() {
    const { fromDateOfService, throughDateOfService } = this.toJSON();
    if (_.isEmpty(throughDateOfService)) {
      this.set("throughDateOfService", fromDateOfService);
    }
  },

  // clear list of claims and force fetching next time the model is valid
  _onDateDiffChange() {
    this.set({
      canFetchClaims: false,
      claimsList: new ClaimsList(),
    });
  },
});

export { Model as default };
